/**
 * Shrinks oversized images in the browser so they fit the server's upload
 * limit, instead of rejecting them.
 *
 * Re-encodes to WebP where supported and JPEG otherwise. Canvas cannot control
 * PNG compression — `toBlob` ignores the quality argument for `image/png` — so
 * a large PNG has to change format to get meaningfully smaller. WebP is
 * preferred because it keeps alpha, which JPEG would flatten to black.
 */

const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

// Product photos never need more than this; going wider costs bytes and buys
// nothing on screen.
const DEFAULT_MAX_DIMENSION = 2400;

const QUALITIES = [0.85, 0.7, 0.55];
const SCALE_ATTEMPTS = 4;
const SCALE_STEP = 0.75;

let webpSupport = null;
const supportsWebp = () => {
    if (webpSupport === null) {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        webpSupport = canvas.toDataURL('image/webp').startsWith('data:image/webp');
    }
    return webpSupport;
};

// `imageOrientation: 'from-image'` applies the EXIF rotation that phone photos
// carry. Drawing to a canvas discards that metadata, so without this a portrait
// photo would come out sideways.
const decode = async (file) => {
    if (typeof createImageBitmap === 'function') {
        try {
            return await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch {
            /* older browsers reject the options bag; fall back below */
        }
    }

    const url = URL.createObjectURL(file);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Could not read image'));
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
};

const toBlob = (canvas, type, quality) =>
    new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const renameFor = (name, type) => {
    const ext = type === 'image/webp' ? 'webp' : 'jpg';
    const base = name.replace(/\.[^./\\]+$/, '') || 'image';
    return `${base}.${ext}`;
};

/**
 * Returns { file, compressed, originalSize }. Files already within the limit
 * are passed straight through untouched. Throws when the image cannot be
 * brought under `maxBytes`.
 */
export const compressImage = async (
    file,
    { maxBytes = DEFAULT_MAX_BYTES, maxDimension = DEFAULT_MAX_DIMENSION } = {}
) => {
    if (file.size <= maxBytes) {
        return { file, compressed: false, originalSize: file.size };
    }

    const source = await decode(file);
    const type = supportsWebp() ? 'image/webp' : 'image/jpeg';

    try {
        const width = source.width;
        const height = source.height;
        let scale = Math.min(1, maxDimension / Math.max(width, height));

        for (let attempt = 0; attempt < SCALE_ATTEMPTS; attempt++) {
            const w = Math.max(1, Math.round(width * scale));
            const h = Math.max(1, Math.round(height * scale));

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Canvas unavailable');

            // JPEG has no alpha; without a white base, transparent regions
            // render as black.
            if (type === 'image/jpeg') {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, w, h);
            }
            ctx.drawImage(source, 0, 0, w, h);

            for (const quality of QUALITIES) {
                const blob = await toBlob(canvas, type, quality);
                if (blob && blob.size <= maxBytes) {
                    return {
                        file: new File([blob], renameFor(file.name, type), { type }),
                        compressed: true,
                        originalSize: file.size,
                    };
                }
            }

            scale *= SCALE_STEP;
        }

        throw new Error('Could not compress below the size limit');
    } finally {
        source.close?.(); // release ImageBitmap memory
    }
};
